import {
  type GetNextPageParamFunction,
  type InfiniteData,
  type QueryFunctionContext,
  type QueryKey,
  type UseQueryOptions,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { type ASTNode, print } from 'graphql';

import Fetcher from '@/api/Fetcher';
import { fetcherSol } from '@/api/sol-fetcher';
import type SolAPIError from '@/error/SolAPIError';

type SolQueryConfig<TVariables> = {
  query?: ASTNode | false;
  variables?: TVariables;
};

type QueryOptions<TData, TError> = Omit<
  UseQueryOptions<TData, TError>,
  'queryKey' | 'placeholderData'
> & {
  keepPreviousData?: boolean;
};

export const QUERY_DEFAULTS = {
  retryOnMount: true,
  refetchOnWindowFocus: false,
  refetchOnReconnect: true,
  refetchInterval: 7_200_000, // 2 hours
};

const isFetcherReady = () => {
  const stytch = Fetcher.getStytch();
  return !!stytch;
};

export const useSolImmutableQuery = <TData = unknown, TVariables = unknown>(
  data: SolQueryConfig<TVariables>,
  options: QueryOptions<TData, SolAPIError> = {},
) => {
  return useSolQuery<TData, TVariables>(data, {
    ...options,
    staleTime: Infinity,
  });
};

export const useSolQuery = <TData = unknown, TVariables = unknown>(
  data: SolQueryConfig<TVariables>,
  { keepPreviousData, ...options }: QueryOptions<TData, SolAPIError> = {},
) => {
  const queryClient = useQueryClient();
  const queryText = data.query && print(data.query);
  const keys = data.variables ? [queryText, data.variables] : [queryText];

  const response = useQuery<TData, SolAPIError>({
    ...options,
    placeholderData: keepPreviousData ? (previousData) => previousData : undefined,
    queryKey: keys,
    queryFn: ({ signal }) =>
      fetcherSol<TVariables, TData>({ query: queryText, variables: data.variables }, { signal }),
    enabled: !!data.query,
  });

  return {
    ...response,
    isLoading: keepPreviousData
      ? response.isLoading || response.isPlaceholderData
      : response.isLoading,
    mutate: (newData?: TData, { revalidate = true } = {}) => {
      if (newData) {
        queryClient.setQueryData(keys, newData);
      }

      if (revalidate) {
        response.refetch();
      }
    },
  };
};

type SolInfiniteQueryParams<TData, TVariables> = {
  queryKey: QueryKey;
  queryFn: (
    context: QueryFunctionContext<QueryKey, number>,
  ) => SolQueryConfig<TVariables> | Promise<SolQueryConfig<TVariables>>;
  initialPageParam?: number;
  getNextPageParam: GetNextPageParamFunction<number, TData>;
};

export const useSolInfiniteQuery = <TData = unknown, TVariables = unknown>({
  queryKey,
  queryFn,
  initialPageParam = 0,
  getNextPageParam,
}: SolInfiniteQueryParams<TData, TVariables>) => {
  return useInfiniteQuery<TData, SolAPIError, InfiniteData<TData, number>, QueryKey, number>({
    queryKey,
    queryFn: async (context: QueryFunctionContext<QueryKey, number>) => {
      context.pageParam ??= 0;
      const response = await queryFn(context);
      return fetcherSol<TVariables, TData>(response);
    },
    initialPageParam,
    getNextPageParam,
  });
};

export const useSolPrefetchInfiniteQuery = <TData = unknown, TVariables = unknown>({
  queryKey,
  queryFn,
  initialPageParam = 0,
  getNextPageParam,
}: SolInfiniteQueryParams<TData, TVariables>) => {
  const queryClient = useQueryClient();

  // If the stytch client is not available, abort the prefetch
  if (!isFetcherReady()) {
    return;
  }

  return queryClient.prefetchInfiniteQuery<
    TData,
    SolAPIError,
    InfiniteData<TData, number>,
    QueryKey,
    number
  >({
    queryKey,
    queryFn: async (context) => {
      context.pageParam ??= 0;
      const response = await queryFn(context);
      return fetcherSol<TVariables, TData>(response);
    },
    initialPageParam,
    getNextPageParam,
  });
};
